<script setup>
import { ref, computed, watch } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import MediaflowAdSchedulesPanel from './MediaflowAdSchedulesPanel.vue'
import MediaflowScheduleEditor from './MediaflowScheduleEditor.vue'
import MediaflowOutputSettings from './MediaflowOutputSettings.vue'
import MediaflowDestinationCard from './MediaflowDestinationCard.vue'
import { upcomingScheduledEvents, formatDurationShort } from './mediaflowMeta'

/**
 * Right-hand panel, one surface: Automation (what fires next, the schedule
 * list, and the schedule editor sliding in with ← back) and Settings (output
 * encoder and destination, edited in place). Escape steps back one level,
 * then closes. It sits one layer below modals (z-40), so a confirm opened from
 * it stacks on top.
 */
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	/** 'automation' | 'settings' */
	tab: { type: String, default: 'automation' },
	mediaflowId: { type: String, required: true },
	schedules: { type: Array, default: () => [] },
	flows: { type: Array, default: () => [] },
	loading: { type: Boolean, default: false },
	maxSchedules: { type: Number, default: 0 },
	togglingId: { type: String, default: null },
	/** Output / destination / schedule deletion are locked while online. */
	locked: { type: Boolean, default: false },
	enabled: { type: Boolean, default: true },
	sessionActive: { type: Boolean, default: false },
	now: { type: Number, default: () => Date.now() },
	mediaflow: { type: Object, default: null },
	destinationStream: { type: Object, default: null },
	disabledStreamIds: { type: Array, default: () => [] },
	hasLiveSubscription: { type: [Object, Boolean], default: false },
	mediaflowResolution: { type: Number, default: 720 },
	fpsOptions: { type: Array, default: () => ['25', '30'] },
	/** e.g. "Fade · 300 ms" */
	transitionLabel: { type: String, default: '' },
})

const emit = defineEmits([
	'update:modelValue', 'update:tab', 'toggle', 'delete', 'schedule-saved', 'set-destination', 'remove-destination', 'open-stream',
])

const open = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) })
const flowName = (id) => props.flows.find((f) => String(f._id) === String(id))?.name || ''
const upcoming = computed(() => upcomingScheduledEvents(props.schedules, flowName, props.now, 6 * 3_600_000, 8))
const inLabel = (at) => {
	const sec = Math.max(0, Math.round((at - props.now) / 1000))
	return sec < 45 ? 'now' : `in ${formatDurationShort(sec)}`
}

// ---------------------------------------------------------------- levels
// Automation: list → editor. Settings: destination picker open or not.
const editor = ref(null)
let editorSeq = 0
const destinationEditing = ref(false)

const openScheduleEditor = ({ schedule = null, breakPrefill = null } = {}) => {
	emit('update:tab', 'automation')
	editor.value = { key: ++editorSeq, schedule, breakPrefill }
}
const openDestinationPicker = () => {
	emit('update:tab', 'settings')
	destinationEditing.value = true
}
const back = () => { editor.value = null }
const onScheduleSaved = () => {
	editor.value = null
	emit('schedule-saved')
}
const setTab = (t) => {
	editor.value = null
	emit('update:tab', t)
}
/** Escape: one level back first (editor → list, picker → card), then close. */
const beforeEscape = () => {
	if (editor.value) { back(); return true }
	if (destinationEditing.value) { destinationEditing.value = false; return true }
	return false
}
watch(open, (v) => {
	if (v) return
	editor.value = null
	destinationEditing.value = false
})

defineExpose({ openScheduleEditor, openDestinationPicker })
</script>

<template>
	<ModalBox
		v-model="open"
		title=""
		aria-label="Automation &amp; settings"
		size="md"
		placement="right"
		scroll-body
		z-index="z-40"
		header-class="items-center gap-3 !pb-3"
		:before-escape="beforeEscape"
	>
		<template #header>
			<div v-if="editor" class="flex min-w-0 flex-1 items-center gap-2" data-testid="side-panel-editor-header">
				<button
					type="button"
					class="-ml-1.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-surface-8 transition-colors hover:bg-surface-3 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
					aria-label="Back to schedules"
					data-testid="side-panel-back"
					@click="back"
				>
					<icon-base width="16" height="16" :width-view-box="24" :height-view-box="24" fill="none" class="rotate-180"><icon-arrow-right /></icon-base>
				</button>
				<h2 class="m-0 truncate text-base font-medium text-surface-text-2">{{ editor.schedule ? 'Edit schedule' : 'New schedule' }}</h2>
			</div>
			<TabGroup v-else type="segmented" size="sm" :cols="2" class="min-w-0 flex-1">
				<TabItem :is-active="tab === 'automation'" :aria-pressed="tab === 'automation'" data-testid="side-panel-tab-automation" @click="setTab('automation')">Automation</TabItem>
				<TabItem :is-active="tab === 'settings'" :aria-pressed="tab === 'settings'" data-testid="side-panel-tab-settings" @click="setTab('settings')">Settings</TabItem>
			</TabGroup>
		</template>

		<div class="flex flex-col gap-3 px-4 pb-4 sm:px-6" :data-testid="editor ? 'side-panel-schedule' : `side-panel-${tab}`">
			<MediaflowScheduleEditor
				v-if="editor"
				:key="editor.key"
				:mediaflow-id="mediaflowId"
				:schedule="editor.schedule"
				:break-prefill="editor.breakPrefill"
				@saved="onScheduleSaved"
				@cancel="back"
			/>

			<template v-else-if="tab === 'automation'">
				<p class="m-0 text-xs text-surface-8">Runs on the server, even with this tab closed.</p>

				<section class="rounded-xl bg-surface-1 p-3" aria-label="Coming up" data-testid="automation-upcoming">
					<h3 class="m-0 mb-2 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">{{ sessionActive ? 'Coming up' : 'Once live' }} · next 6 hours</h3>
					<p v-if="!upcoming.length" class="m-0 text-xs text-surface-8">Nothing in the next 6 hours.</p>
					<ol v-else class="m-0 flex list-none flex-col gap-1.5 p-0">
						<li v-for="ev in upcoming" :key="ev.id" class="flex items-center gap-2 text-xs" data-testid="automation-event">
							<span class="w-11 shrink-0 font-mono tabular-nums text-surface-text-2">{{ ev.atLabel }}</span>
							<span class="size-1.5 shrink-0 rounded-full" :class="ev.kind === 'ad' ? 'bg-error' : 'bg-main-light'" aria-hidden="true" />
							<span class="min-w-0 flex-1 truncate text-surface-text-1" :title="ev.schedule.name">{{ ev.action }}<span v-if="ev.returnsTo" class="ml-1 text-surface-7">→ {{ ev.returnsTo }}</span></span>
							<span class="shrink-0 text-surface-7">{{ inLabel(ev.at) }}</span>
						</li>
					</ol>
				</section>

				<MediaflowAdSchedulesPanel
					:schedules="schedules"
					:flows="flows"
					:max-schedules="maxSchedules"
					:toggling-id="togglingId"
					:delete-locked="locked"
					:enabled="enabled"
					:loading="loading && !schedules.length"
					@add="openScheduleEditor()"
					@toggle="emit('toggle', $event)"
					@edit="openScheduleEditor({ schedule: $event })"
					@delete="emit('delete', $event)"
				/>
			</template>

			<template v-else>
				<MediaflowOutputSettings
					v-if="mediaflow"
					:mediaflow="mediaflow"
					:mediaflow-resolution="mediaflowResolution"
					:fps-options="fpsOptions"
					:locked="locked"
					:enabled="enabled"
				/>
				<MediaflowDestinationCard
					v-model:editing="destinationEditing"
					:destination-stream="destinationStream"
					:disabled-stream-ids="disabledStreamIds"
					:has-live-subscription="hasLiveSubscription"
					:enabled="enabled"
					:locked="locked"
					@set-destination="emit('set-destination', $event)"
					@remove-destination="emit('remove-destination')"
					@open-stream="emit('open-stream')"
				/>
				<p v-if="transitionLabel" class="m-0 px-1 text-xs text-surface-8" data-testid="settings-transition">Default transition: <span class="text-surface-text-1">{{ transitionLabel }}</span></p>
			</template>
		</div>
	</ModalBox>
</template>
